import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Tooltip
} from '@mui/material';
import {
  CalendarToday,
  Person,
  Assessment,
  Inventory,
  LocationOn,
  Settings,
  Visibility,
  Search,
  ContactMail,
  ShoppingBag,
  MedicalInformation as MedicalInformationIcon,
  Security as SecurityIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Colors } from '../../styles/theme';
import { useCan } from '../../hooks/useCan';
import { useModuleAccess } from 'hooks/useModuleAccess';

/**
 * Dashboard Component
 *
 * Main dashboard for the optometry management application.
 * Displays configurable function cards in a grid layout with icons and navigation.
 * Users can customize which functions are displayed through a settings dialog.
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const can = useCan();
  const moduleAccess = useModuleAccess(); // Custom hook to get module access permissions
  // Access checks per function id; fallback to permissive if unknown id
  const accessChecks = useMemo(() => ({
    appointments: () => can.any([
      { section: 'appointments', level: 'read' },
      { section: 'appointment', level: 'read' }
    ]),
    patients: () => can.any([
      { section: 'customers', level: 'read' },
      { section: 'patient', level: 'read' },
      { section: 'patients', level: 'read' }
    ]),
    inventory: () => can.any([
      { section: 'inventory', level: 'read' }
    ]),
    reporting: () => can.any([
      { section: 'reporting', level: 'read' }
    ]),
    frames: () => can.any([
      { section: 'inventory', level: 'write' }
    ]),
    lenses: () => can.any([
      { section: 'inventory', level: 'write' }
    ]),
    contacts: () => can.any([
      { section: 'inventory', level: 'write' }
    ]),
    accessories: () => can.any([
      { section: 'inventory', level: 'write' }
    ]),
    locations: () => can.any([
      { section: 'locations', level: 'read' }
    ]),
    security: () => can.any([
      { section: 'security', level: 'read' }
    ]),
    audit: () => can.any([
      { section: 'audit', level: 'read' }
    ]),
    patientChart: () => can.any([
      { section: 'eyeexam', level: 'read' },
      { section: 'chart', level: 'read' }
    ]),
    // New tiles
    recentActivity: () => true, // route is public for authenticated users
    examTemplates: () => can.action('eyeexam', 'manage-templates')
  }), [can]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [availableFunctions, setAvailableFunctions] = useState([
    { id: 'appointments', title: 'Appointments', icon: CalendarToday, enabled: true, route: '/appointments', section: 'Daily Work' },
    { id: 'patients', title: 'Patient Records', icon: Person, enabled: true, route: '/customerrecord', section: 'Daily Work' },
    { id: 'inventory', title: 'Inventory', icon: Inventory, enabled: true, route: '/inventory', section: 'Daily Work', module: 'inventory' },
    { id: 'reporting', title: 'Reports', icon: Assessment, enabled: true, route: '/reporting', section: 'Reporting' },
    { id: 'frames', title: 'Frames', icon: Visibility, enabled: true, route: '/inventory?subtab=frames', section: 'Inventory Setup', module: 'inventory' },
    { id: 'lenses', title: 'Lenses', icon: Search, enabled: true, route: '/inventory?subtab=lenses', section: 'Inventory Setup', module: 'inventory' },
    { id: 'contacts', title: 'Contacts', icon: ContactMail, enabled: true, route: '/inventory?subtab=contacts', section: 'Inventory Setup', module: 'inventory' },
    { id: 'accessories', title: 'Accessories', icon: ShoppingBag, enabled: true, route: '/inventory?subtab=accessories', section: 'Inventory Setup', module: 'inventory' },
    { id: 'locations', title: 'Locations', icon: LocationOn, enabled: true, route: '/locations', section: 'Administration' },
    { id: 'security', title: 'Security', icon: SecurityIcon, enabled: true, route: '/security', section: 'Administration' },
    { id: 'audit', title: 'Audit Logs', icon: HistoryIcon, enabled: true, route: '/audit', section: 'Administration' },
    { id: 'patientChart', title: 'Patient Chart', icon: MedicalInformationIcon, enabled: true, route: '/customerrecord?tab=chart', section: 'Daily Work' }
    ,
    // New: Recent Activity and Exam Templates
    { id: 'recentActivity', title: 'Recent Activity', icon: HistoryIcon, enabled: true, route: '/activity', section: 'Daily Work', module: 'activity' },
    { id: 'examTemplates', title: 'Exam Templates', icon: Assessment, enabled: true, route: '/templates', section: 'Administration' }
  ]);

  // Load user preferences from localStorage on component mount
  // TODO: After authentication is implemented, replace localStorage with user preferences API
  // This will allow dashboard customization to be saved per user account
  useEffect(() => {
    const savedPreferences = localStorage.getItem('dashboardFunctions');
    if (savedPreferences) {
      try {
        const preferences = JSON.parse(savedPreferences);
        setAvailableFunctions(prev =>
          prev.map(func => ({
            ...func,
            enabled: preferences[func.id] !== false
          }))
        );
      } catch (error) {
        console.warn('Failed to load dashboard preferences:', error);
      }
    }
  }, []);

  // Save user preferences to localStorage whenever functions change
  // TODO: After authentication is implemented, replace localStorage with user preferences API
  // This will allow dashboard customization to be saved per user account and synced across devices
  useEffect(() => {
    const preferences = availableFunctions.reduce((acc, func) => {
      acc[func.id] = func.enabled;
      return acc;
    }, {});
    localStorage.setItem('dashboardFunctions', JSON.stringify(preferences));
  }, [availableFunctions]);

  /**
   * Handle navigation to a specific function
   * @param {string} route - The route to navigate to
   */
  const handleFunctionClick = (route) => {
    navigate(route);
  };

  /**
   * Toggle the enabled state of a function
   * @param {string} functionId - The ID of the function to toggle
   */
  const toggleFunction = (functionId) => {
    setAvailableFunctions(prev =>
      prev.map(func =>
        func.id === functionId
          ? { ...func, enabled: !func.enabled }
          : func
      )
    );
  };

  // Group enabled functions by section for display
  const groupedEnabledFunctions = useMemo(() => {
    const enabled = availableFunctions
      .filter(func => func.enabled)
      .filter(func => !func.module || moduleAccess.isEnabled(func.module))
      .filter(func => (accessChecks[func.id] ? accessChecks[func.id]() : true));
    return enabled.reduce((acc, func) => {
      const key = func.section || 'General';
      if (!acc[key]) acc[key] = [];
      acc[key].push(func);
      return acc;
    }, {});
  }, [availableFunctions, accessChecks, moduleAccess]);

  /**
   * Open the settings dialog
   */
  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  /**
   * Close the settings dialog
   */
  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Dashboard Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ color: Colors.primary }}>
          Dashboard
        </Typography>
        <Tooltip title="Customize Dashboard">
          <IconButton
            onClick={handleOpenSettings}
            sx={{
              bgcolor: Colors.primary,
              color: 'white',
              '&:hover': {
                bgcolor: Colors.primary,
                opacity: 0.8
              }
            }}
          >
            <Settings />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Grouped Function Cards */}
      {Object.entries(groupedEnabledFunctions).map(([section, funcs]) => (
        <Box key={section} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, color: Colors.muted }}>
            {section}
          </Typography>
          <Grid container spacing={3}>
            {funcs.map((func) => {
              const IconComponent = func.icon;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={func.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        borderColor: Colors.primary
                      },
                      border: `1px solid ${Colors.muted}`,
                      height: '100%'
                    }}
                    onClick={() => handleFunctionClick(func.route)}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <IconComponent
                        sx={{
                          fontSize: 48,
                          color: Colors.primary,
                          mb: 2
                        }}
                      />
                      <Typography variant="h6" component="h2" gutterBottom>
                        {func.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manage {func.title.toLowerCase()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={handleCloseSettings}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Customize Dashboard</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which functions to display on your dashboard:
          </Typography>
          <List>
            {availableFunctions
              // Hide from settings if user cannot access
              .filter(func => !func.module || moduleAccess.isEnabled(func.module))
              .filter(func => (accessChecks[func.id] ? accessChecks[func.id]() : true))
              .map((func) => (
              <ListItem key={func.id} dense>
                <ListItemText primary={func.title} />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge="end"
                    checked={func.enabled}
                    onChange={() => toggleFunction(func.id)}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}